import { useEffect, useId, useState, type ReactNode, type Ref } from "react";

import { Button } from "./Button";
import { Field } from "./Field";
import { Input } from "./FormControls";
import { HelpText } from "./HelpText";

export interface OverridableInputProps {
  label: string;
  hint?: string;
  /** Stored override, if any. */
  override: string | undefined;
  /** What is shown while there is no override. */
  fallback: string;
  actions?: ReactNode;
  endAdornment?: ReactNode;
  inputRef?: Ref<HTMLInputElement>;
  disabled?: boolean;
  showReset?: boolean;
  /** Receives the new override, or `null` to go back to the default. */
  onChange: (value: string | null) => void;
}

/**
 * A text input for a value that has a default. It always shows what is currently
 * displayed (the default until reworded) so editing shows its effect at once;
 * emptying it or typing the default again removes the override.
 */
export function OverridableInput({
  label,
  hint,
  override,
  fallback,
  actions,
  endAdornment,
  inputRef,
  disabled,
  showReset = true,
  onChange,
}: OverridableInputProps) {
  const id = useId();
  const shown = override && override.trim() ? override : fallback;
  const [draft, setDraft] = useState(shown);
  useEffect(() => setDraft(shown), [shown]);
  const edited = Boolean(override && override.trim() && override !== fallback);
  return (
    <Field label={label} htmlFor={id}>
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Input
            id={id}
            ref={inputRef}
            disabled={disabled}
            className={endAdornment ? "pr-11" : undefined}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              if (event.target.value.trim())
                onChange(event.target.value === fallback ? null : event.target.value);
            }}
            onBlur={() => {
              if (!draft.trim()) {
                setDraft(fallback);
                onChange(null);
              }
            }}
          />
          {endAdornment ? (
            <div className="absolute inset-y-0 right-1 flex items-center">{endAdornment}</div>
          ) : null}
        </div>
        {actions}
        {edited && showReset ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={disabled}
            onClick={() => onChange(null)}
          >
            Reset
          </Button>
        ) : null}
      </div>
      {hint ? <HelpText className="mt-1.5">{hint}</HelpText> : null}
    </Field>
  );
}
